import { CmsProduct } from '../../../types/cmsProduct';
import { PushDataLayerFn } from '../../../types/pushDataLayer';
import type { ResolvedGroup } from '../utils/resolveAttributeRow/resolveAttributeRow';
import { ProductDetailsAttributeRow } from './ProductDetailsAttributeRow';

type ProductDetailsAttributeGroupBodyProps = {
  group: ResolvedGroup;
  /** Section title, forwarded to every row for its analytics events. */
  sectionTitle: string;
  product?: CmsProduct;
  pushDataLayer?: PushDataLayerFn;
};

/** Card body of one group: optional caption + the resolved rows in config order. */
export const ProductDetailsAttributeGroupBody: React.FC<
  ProductDetailsAttributeGroupBodyProps
> = ({ group, sectionTitle, product, pushDataLayer }) => {
  // A titled group owns the <h3> (accordion toggle or the caption below), so its rows step down
  // to <h4>; rows of an untitled group are the card's top-level headings.
  const rowHeadingLevel = group.title ? 'h4' : 'h3';

  return (
    <div
      className="py-spacing/padding/y px-spacing/padding/x flex flex-col gap-spacing/gap/x"
      data-component="pdp-configured-group"
    >
      {/* A non-collapsible titled group shows its title as a caption inside the card
          (Figma: "INTEREST RATES", "PLATFORM DETAIL"); the accordion already renders it. */}
      {group.title && !group.collapsible && (
        <h3 className="alpha-subheading-2 text-text/accent m-0">
          {group.title}
        </h3>
      )}
      {group.rows.map((row, index) => (
        <ProductDetailsAttributeRow
          // Rows are a static, ordered config; the index only disambiguates duplicates.
          // eslint-disable-next-line react/no-array-index-key
          key={`${row.attributeKey}-${index}`}
          row={row}
          isLast={index === group.rows.length - 1}
          headingLevel={rowHeadingLevel}
          sectionTitle={sectionTitle}
          product={product}
          pushDataLayer={pushDataLayer}
        />
      ))}
    </div>
  );
};
